import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { switchMap } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';

import { ENDPOINTS } from '@shared/constants';
import {
  CommonResponseDTO,
  IAttendance,
  IAttendanceConfiguration,
} from '@shared/interfaces';
import { generateURL } from '@shared/utils';

import { LoggedUserService } from '../../auth/services';
export interface AuthorizerNameMap {
  id: string;
  name: string;
}

export interface AttendanceFileDetailsResponse {
  data: IAttendance;
  authorizerNames: AuthorizerNameMap[];
}
@Injectable({
  providedIn: 'root',
})
export class AttendanceService {
  private uploadedDataListSubject = new BehaviorSubject<IAttendance[]>([]);
  uploadedDataList$ = this.uploadedDataListSubject.asObservable();

  private uploadSuccessSubject = new BehaviorSubject<boolean>(false);
  uploadSuccess$ = this.uploadSuccessSubject.asObservable();
  constructor(
    private http: HttpClient,
    private loggedUserService: LoggedUserService
  ) {}

  // ================================= Attendance Services ======================================

  // Method to submit attendance data (Clock-in)
  public clockIn(
    clockInData: IAttendance
  ): Observable<CommonResponseDTO<IAttendance>> {
    const url = generateURL({ endpoint: ENDPOINTS.ATTENDANCE_CREATE_NEW });
    return this.http.post<CommonResponseDTO<IAttendance>>(url, clockInData);
  }

  setUploadedDataList(data: IAttendance[]) {
    this.uploadedDataListSubject.next(data);
  }

  setUploadSuccess(success: boolean) {
    this.uploadSuccessSubject.next(success);
  }
  // Method to submit clock-out data
  public clockOut(
    clockOutData: IAttendance
  ): Observable<CommonResponseDTO<IAttendance>> {
    return this.loggedUserService.dataStore.pipe(
      switchMap((user) => {
        if (user && user._id) {
          const userId = user._id;
          const url = generateURL({
            endpoint: ENDPOINTS.ATTENDANCE_UPDATE,
            params: { id: userId },
          });

          // Make PUT request to update attendance with clock-out data
          return this.http.put<CommonResponseDTO<IAttendance>>(
            url,
            clockOutData
          );
        } else {
          throw new Error('User not logged in');
        }
      })
    );
  }

  public getAllAttendance(
    params: HttpParams
  ): Observable<CommonResponseDTO<IAttendance[]>> {
    const url = generateURL({ endpoint: ENDPOINTS.ATTENDANCE_GET_ALL });
    const config = { params };

    return this.http.get<CommonResponseDTO<IAttendance[]>>(url, config);
  }

  public checkClockedInStatus(): Observable<CommonResponseDTO<IAttendance[]>> {
    const url = generateURL({
      endpoint: ENDPOINTS.ATTENDANCE_GET_ALL,
    });
    return this.http.get<CommonResponseDTO<IAttendance[]>>(url);
  }

  public checkUserClockInStatus(userId: string): Observable<{
    success: boolean;
    isActive: boolean;
    clockedInTime?: Date;
    // clockInCount: number;
  }> {
    // Generating the URL to check if the user has an active clock-in
    const url = generateURL({
      endpoint: ENDPOINTS.ATTENDANCE_FIND_ACTIVE_CLOCKINS,
      params: { id: userId },
    });

    // Make GET request to the backend and return the Observable
    return this.http.get<{
      success: boolean;
      isActive: boolean;
      clockedInTime?: Date;
      // clockInCount: number;
    }>(url);
  }

  public getClockInCount(userId: string): Observable<{ clockInCount: number }> {
    const url = generateURL({
      endpoint: ENDPOINTS.ATTENDANCE_CLOCKIN_COUNT, // Add a new endpoint in your API endpoints file
      params: { id: userId },
    });
    return this.http.get<{ clockInCount: number }>(url);
  }

  //================================= Attendance Bulk Upload Services ===========================

  public getAllDuplicateAttendance(
    startDate: string,
    endDate: string,
    employeeNumbers: string[]
  ): Observable<CommonResponseDTO<IAttendance[]>> {
    let params = new HttpParams();
    params = params.append('startDate', startDate);
    params = params.append('endDate', endDate);
    params = params.append('employeeNumbers', employeeNumbers.join(','));
    const config = { params };
    const url = generateURL({
      endpoint: ENDPOINTS.ATTENDANCE_BULK_UPLOAD_GET_ALL_DUPLICATES,
    });

    return this.http.get<CommonResponseDTO<IAttendance[]>>(url, config);
  }

  //get all records according to the date range and employee numbers
  public getAllRecords(
    startDate: string,
    endDate: string,
    employeeNumbers: string[]
  ): Observable<CommonResponseDTO<IAttendance[]>> {
    let params = new HttpParams();
    params = params.append('startDate', startDate);
    params = params.append('endDate', endDate);
    params = params.append('employeeNumbers', employeeNumbers.join(','));
    const config = { params };
    const url = generateURL({
      endpoint: ENDPOINTS.ATTENDANCE_BULK_UPLOAD_GET_ALL_RECORDS,
    });

    return this.http.get<CommonResponseDTO<IAttendance[]>>(url, config);
  }

  public uploadBulkAttendance(
    fileData: IAttendance[],
    startDate: string,
    endDate: string,
    employeeNumbers: string[]
  ): Observable<CommonResponseDTO<IAttendance[]>> {
    const url = generateURL({
      endpoint: ENDPOINTS.ATTENDANCE_BULK_UPLOAD_CREATE_NEW,
    });
    let params = new HttpParams();
    params = params.append('startDate', startDate);
    params = params.append('endDate', endDate);
    params = params.append('employeeNumbers', employeeNumbers.join(','));
    const config = { params };
    return this.http.post<CommonResponseDTO<IAttendance[]>>(
      url,
      fileData,
      config
    );
  }
  public updateAttendanceSelection(
    attendanceIds: string[],
    startDate: string,
    endDate: string
  ): Observable<CommonResponseDTO<IAttendance>> {
    let params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    attendanceIds.forEach((id) => {
      params = params.append('id', id);
    });

    const url = generateURL({
      endpoint: ENDPOINTS.ATTENDANCE_BULK_UPLOAD_UPDATE_SELECTION,
    });

    return this.http.put<CommonResponseDTO<IAttendance>>(url, null, { params });
  }

  public updateBulkAttendanceSelection(
    attendanceData: IAttendance[],
    startDate: string,
    endDate: string
  ): Observable<CommonResponseDTO<IAttendance>> {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);
    const body = {
      attendanceData,
    };
    const url = generateURL({
      endpoint: ENDPOINTS.ATTENDANCE_UPDATE_SELECTION,
    });

    return this.http.put<CommonResponseDTO<IAttendance>>(url, body, { params });
  }

  public addAuthorizersToAttendance(
    authorizerIDs: string[],
    remarks: string,
    startDate: string,
    endDate: string,
    employeeNumbers: string[]
  ): Observable<CommonResponseDTO<IAttendance>> {
    let params = new HttpParams();
    params = params.append('startDate', startDate);
    params = params.append('endDate', endDate);
    const config = { params };
    const body = {
      authorizerIDs,
      remarks,
      employeeNumbers,
    };
    const url = generateURL({
      endpoint: ENDPOINTS.ATTENDANCE_BULK_UPLOAD_ADD_AUTHORIZERS,
    });

    return this.http.put<CommonResponseDTO<IAttendance>>(url, body, config);
  }

  public updateAuthorizersToAttendance(
    authorizerIDs: string[],
    remarks: string,
    startDate: string,
    endDate: string,
    batchId: string,
    employeeNumbers: string[]
  ): Observable<CommonResponseDTO<IAttendance>> {
    let params = new HttpParams();
    params = params.append('startDate', startDate);
    params = params.append('endDate', endDate);
    const config = { params };
    const body = {
      authorizerIDs,
      remarks,
      batchId,
      employeeNumbers,
    };
    const url = generateURL({
      endpoint: ENDPOINTS.ATTENDANCE_APPROVAL_REQUEST_UPDATE_AUTHORIZERS,
    });

    return this.http.put<CommonResponseDTO<IAttendance>>(url, body, config);
  }

  //====================== Attendance Configuration Services ===========================
  public createAttendanceConfiguration(
    attendanceConfiguration: IAttendanceConfiguration
  ): Observable<CommonResponseDTO<IAttendanceConfiguration>> {
    const url = generateURL({
      endpoint: ENDPOINTS.ATTENDANCE_CONFIGURATIONS_CREATE_NEW,
    });
    return this.http.post<CommonResponseDTO<IAttendanceConfiguration>>(
      url,
      attendanceConfiguration
    );
  }

  public getLatestAttendanceConfiguration(): Observable<
    CommonResponseDTO<IAttendanceConfiguration>
  > {
    const url = generateURL({
      endpoint: ENDPOINTS.ATTENDANCE_CONFIGURATIONS_GET_ALL,
    });
    return this.http.get<CommonResponseDTO<IAttendanceConfiguration>>(url);
  }

  // ================================= Attendance Files Services ======================================
  public getAttendanceFiles(
    params: HttpParams
  ): Observable<CommonResponseDTO<IAttendance[]>> {
    const url = generateURL({ endpoint: ENDPOINTS.ATTENDANCE_GET_ALL_FILES });
    const config = { params };

    return this.http.get<CommonResponseDTO<IAttendance[]>>(url, config);
  }

  public getAttendanceFileDetailsById(
    id: string
  ): Observable<CommonResponseDTO<AttendanceFileDetailsResponse>> {
    const url = generateURL({
      endpoint: ENDPOINTS.ATTENDANCE_GET_FILE_DETAILS,
      params: { id },
    });
    return this.http.get<CommonResponseDTO<AttendanceFileDetailsResponse>>(url);
  }

  public getAttendanceDuplicatesForApproval(
    documentId: string
  ): Observable<CommonResponseDTO<IAttendance[]>> {
    const url = generateURL({
      endpoint: ENDPOINTS.ATTENDANCE_GET_DUPLICATE_FOR_APPROVAL,
      params: { id: documentId },
    });
    return this.http.get<CommonResponseDTO<IAttendance[]>>(url);
  }

  public getAllRecordsForApproval(
    startDate: string,
    endDate: string,
    employeeNumbers: string[],
    documentId: string
  ): Observable<CommonResponseDTO<IAttendance[]>> {
    let params = new HttpParams();
    params = params.append('startDate', startDate);
    params = params.append('endDate', endDate);
    params = params.append('documentId', documentId);
    params = params.append('employeeNumbers', employeeNumbers.join(','));
    const config = { params };
    const url = generateURL({
      endpoint: ENDPOINTS.ATTENDANCE_GET_ALL_FOR_APPROVAL,
    });

    return this.http.get<CommonResponseDTO<IAttendance[]>>(url, config);
  }

  public addApprovalRequestDecision(
    documentId: string,
    selected_option: string,
    remarks: string
  ): Observable<CommonResponseDTO<IAttendance>> {
    const config = {
      selected_option,
      remarks,
    };
    const url = generateURL({
      endpoint: ENDPOINTS.ATTENDANCE_ADD_APPROVAL_REQUEST_DECISION,
      params: { id: documentId },
    });
    return this.http.put<CommonResponseDTO<IAttendance>>(url, config);
  }
}
